import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  EmptyState,
  SectionHeader,
  TextField,
  TextareaField,
  SelectField,
} from "../../components/design-system/index.js";
import { ConfirmButton } from "../../components/confirm-button.js";
import { Toast, useToast } from "../../components/toast.js";
import {
  createEstateItem,
  deleteEstateItem,
  listEstateItems,
  updateEstateItem,
  type EstateDigitalAssetItem,
} from "../../lib/api-modules/estate.js";
import { listAccounts } from "../../lib/api-modules/accounts.js";

/**
 * /estate/digital-assets — Phase 9.
 *
 * Per-account (or free-form) recovery instructions: vault location,
 * recovery codes location, etc. Hearth NEVER stores raw passwords —
 * the placeholder text + helper guard against the temptation.
 */
export function EstateDigitalAssetsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const itemsQ = useQuery({
    queryKey: ["estate-items"],
    queryFn: listEstateItems,
  });
  const accountsQ = useQuery({
    queryKey: ["accounts"],
    queryFn: () => listAccounts(),
  });

  const digitalAssets =
    (itemsQ.data?.items.filter(
      (i) => i.item_type === "digital_asset",
    ) as EstateDigitalAssetItem[]) ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <Link
        to="/estate"
        className="inline-flex items-center text-sm text-[var(--color-accent)]"
      >
        ← Back to estate
      </Link>
      <SectionHeader
        title="Digital assets inventory"
        sub="Per-account recovery instructions for whoever needs to access your accounts. Pointers only — never raw passwords."
      />
      <Toast message={toast.message} />

      <Card className="bg-[var(--color-warning-soft)]/40 text-sm text-[var(--color-warning)]">
        Use this for pointers like &ldquo;1Password vault, master password in
        safe deposit box at Citi 5th Ave branch&rdquo; or &ldquo;recovery
        codes printed + filed with attorney.&rdquo; Do NOT paste actual
        passwords here.
      </Card>

      <DigitalAssetForm
        accounts={accountsQ.data?.accounts ?? []}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ["estate-items"] });
          toast.show("Digital asset added");
        }}
      />

      <section className="space-y-3">
        {digitalAssets.length === 0 ? (
          <EmptyState
            title="No digital assets recorded yet"
            description="Add at least your password vault location + email-recovery details."
          />
        ) : (
          digitalAssets.map((d) => (
            <DigitalAssetRow
              key={d.item_id}
              item={d}
              accounts={accountsQ.data?.accounts ?? []}
              onUpdated={() => {
                void queryClient.invalidateQueries({
                  queryKey: ["estate-items"],
                });
                toast.show("Updated");
              }}
              onDeleted={() => {
                void queryClient.invalidateQueries({
                  queryKey: ["estate-items"],
                });
                toast.show("Removed");
              }}
            />
          ))
        )}
      </section>
    </div>
  );
}

interface AccountSummary {
  account_id: string;
  name: string;
  institution?: string;
}

function DigitalAssetForm({
  accounts,
  onCreated,
}: {
  accounts: AccountSummary[];
  onCreated: () => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [linkedAccountId, setLinkedAccountId] = useState("");
  const [recoveryInstructions, setRecoveryInstructions] = useState("");
  const createMut = useMutation({
    mutationFn: () =>
      createEstateItem({
        item_type: "digital_asset",
        display_name: displayName.trim(),
        linked_account_id: linkedAccountId || undefined,
        recovery_instructions: recoveryInstructions.trim(),
      }),
    onSuccess: () => {
      setDisplayName("");
      setLinkedAccountId("");
      setRecoveryInstructions("");
      onCreated();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !recoveryInstructions.trim()) return;
    createMut.mutate();
  };
  return (
    <Card>
      <form onSubmit={onSubmit} className="space-y-3">
        <TextField
          label="Display name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. 1Password vault"
        />
        <SelectField
          label="Linked account (optional)"
          value={linkedAccountId}
          onChange={(e) => setLinkedAccountId(e.target.value)}
        >
          <option value="">— None —</option>
          {accounts.map((a) => (
            <option key={a.account_id} value={a.account_id}>
              {a.name} {a.institution ? `(${a.institution})` : ""}
            </option>
          ))}
        </SelectField>
        <TextareaField
          label="Recovery instructions"
          required
          rows={4}
          value={recoveryInstructions}
          onChange={(e) => setRecoveryInstructions(e.target.value)}
          placeholder="Pointer to recovery info — vault location, where backup codes live, who has the master password. NEVER paste the password itself."
        />
        <div>
          <Button
            type="submit"
            size="sm"
            disabled={
              createMut.isPending ||
              !displayName.trim() ||
              !recoveryInstructions.trim()
            }
          >
            {createMut.isPending ? "Adding…" : "Add digital asset"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function DigitalAssetRow({
  item,
  accounts,
  onUpdated,
  onDeleted,
}: {
  item: EstateDigitalAssetItem;
  accounts: AccountSummary[];
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(item.display_name);
  const [linkedAccountId, setLinkedAccountId] = useState(
    item.linked_account_id ?? "",
  );
  const [recoveryInstructions, setRecoveryInstructions] = useState(
    item.recovery_instructions,
  );
  const updateMut = useMutation({
    mutationFn: () =>
      updateEstateItem(item.item_id, {
        display_name: displayName.trim(),
        linked_account_id: linkedAccountId || undefined,
        recovery_instructions: recoveryInstructions.trim(),
      }),
    onSuccess: () => {
      setEditing(false);
      onUpdated();
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteEstateItem(item.item_id),
    onSuccess: onDeleted,
  });
  const linkedAccount = accounts.find((a) => a.account_id === item.linked_account_id);
  return (
    <Card>
      {!editing ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-[var(--color-text)]">
                {item.display_name}
              </p>
              {linkedAccount && (
                <p className="mt-0.5 text-xs text-[var(--color-muted)]">
                  Linked to {linkedAccount.name}
                  {linkedAccount.institution
                    ? ` (${linkedAccount.institution})`
                    : ""}
                </p>
              )}
            </div>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text)]">
            {item.recovery_instructions}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <ConfirmButton
              label="Remove"
              confirmLabel="Yes, remove"
              onConfirm={() => deleteMut.mutate()}
              isPending={deleteMut.isPending}
            />
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <SelectField
            label="Linked account (optional)"
            value={linkedAccountId}
            onChange={(e) => setLinkedAccountId(e.target.value)}
          >
            <option value="">— None —</option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.name}
              </option>
            ))}
          </SelectField>
          <TextareaField
            label="Recovery instructions"
            rows={4}
            value={recoveryInstructions}
            onChange={(e) => setRecoveryInstructions(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => updateMut.mutate()}
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={updateMut.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
